import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../../Hooks/useTheme";
import { ROUTES } from "../../Constants/Routes";
import "./Navbar.css";
import logo from "../../Assets/lico-technologies-logo.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 968) {
        setIsMobileMenuOpen(false);
        setIsMobileDropdownOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navLinks = [
    { label: "Home", path: ROUTES.HOME.path },
    { label: "About", path: ROUTES.ABOUT.path },
    { label: "Blog", path: ROUTES.BLOGS.path },
    { label: "Faq", path: ROUTES.FAQ.path },
    { label: "Contact", path: ROUTES.CONTACTUS.path },
  ];

  const dropdownLinks = [
    { label: "Services", path: ROUTES.SERVICES.path },
    // { label: "Training", path: ROUTES.TRAINING.path },
    { label: "Products / Apps", path: ROUTES.PRODUCTS.path },
  ];

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    path: string
  ) => {
    if (path.startsWith("#")) {
      e.preventDefault();
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      setIsMobileMenuOpen(false);
      setIsDropdownOpen(false);
    }
  };

  return (
    <>
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`} id="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <img src={logo} alt="LICO Logo" className="logo-image" />
            <div className="logo-details">
              <span className="logo-name">LICO</span>
              <span className="logo-subtitle">Learn & Innovate</span>
            </div>
          </Link>

          <div className="nav-center">
            <ul className="nav-menu">
              {navLinks.slice(0, 2).map((link) => (
                <li key={link.path}>
                  <Link to={link.path}>{link.label}</Link>
                </li>
              ))}

              {/* Desktop Dropdown */}
              <li
                className="nav-dropdown"
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <span className="nav-dropdown-toggle">
                  What We Do
                  <svg
                    className={`dropdown-arrow ${isDropdownOpen ? "open" : ""}`}
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L6 6L11 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <ul
                  className={`dropdown-menu ${isDropdownOpen ? "active" : ""}`}
                >
                  {dropdownLinks.map((link) => (
                    <li key={link.path}>
                      {link.path.startsWith("#") ? (
                        <a
                          href={link.path}
                          onClick={(e) => handleNavClick(e, link.path)}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link to={link.path}>{link.label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </li>

              {navLinks.slice(2).map((link) => (
                <li key={link.path}>
                  {link.path.startsWith("#") ? (
                    <a
                      href={link.path}
                      onClick={(e) => handleNavClick(e, link.path)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.path}>{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>

            <div className="nav-actions">
              <button
                className="theme-toggle"
                id="themeToggle"
                onClick={toggleTheme}
                aria-label="Toggle theme"
              >
                <span id="themeIcon">{theme === "dark" ? "☀️" : "🌙"}</span>
              </button>
              <button
                className={`mobile-menu-btn ${
                  isMobileMenuOpen ? "active" : ""
                }`}
                id="mobileMenuBtn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${isMobileMenuOpen ? "active" : ""}`}
        id="mobileMenu"
      >
        <ul className="mobile-menu-list">
          {navLinks.slice(0, 2).map((link) => (
            <li key={link.path}>
              <Link to={link.path} onClick={() => setIsMobileMenuOpen(false)}>
                {link.label}
              </Link>
            </li>
          ))}

          {/* Mobile Dropdown */}
          <li className="mobile-dropdown">
            <button
              className="mobile-dropdown-toggle"
              onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
            >
              What We Do
              <svg
                className={`dropdown-arrow ${
                  isMobileDropdownOpen ? "open" : ""
                }`}
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L6 6L11 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <ul
              className={`mobile-dropdown-menu ${
                isMobileDropdownOpen ? "active" : ""
              }`}
            >
              {dropdownLinks.map((link) => (
                <li key={link.path}>
                  {link.path.startsWith("#") ? (
                    <a
                      href={link.path}
                      onClick={(e) => {
                        handleNavClick(e, link.path);
                        setIsMobileDropdownOpen(false);
                      }}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setIsMobileDropdownOpen(false);
                      }}
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </li>

          {navLinks.slice(2).map((link) => (
            <li key={link.path}>
              {link.path.startsWith("#") ? (
                <a
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                >
                  {link.label}
                </a>
              ) : (
                <Link to={link.path} onClick={() => setIsMobileMenuOpen(false)}>
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default Navbar;
